/**
 * Time-off plans and enrollment (piece 34, *PTO: plans and enrollment*;
 * decision 13130; row T3904). 🔒 Settings › HR › Time Off Plans lists every
 * plan and edits it, never adds one, for a holder of `hr.hire`. 🔒 HR reading
 * as HR with `hr.hire` alone enrolls a job from its Job section: the plan, the
 * enrollment date, a reason and the opening balance, which is written as one
 * OpeningBalance ledger row nobody edits afterwards. An enrollment is ended,
 * never deleted.
 */

import { fill, t } from '../strings';
import { isCalendarDay } from '../zonedTime';

/** The Settings entry's own address. */
export const TIME_OFF_PLANS_ROUTE = '/settings/hr/time-off-plans';
/** Every plan, relative to the API root. */
export const TIME_OFF_PLANS_PATH = '/hr/time-off-plans';

/** One plan's edit, relative to the API root. */
export function timeOffPlanPath(planId: string): string {
  return `${TIME_OFF_PLANS_PATH}/${encodeURIComponent(planId)}`;
}

/** Enroll one job in a plan, relative to the API root. */
export function timeOffEnrollmentsPath(employmentId: string): string {
  return `/hr/employees/${encodeURIComponent(employmentId)}/time-off-enrollments`;
}

/** End one enrollment of one job, relative to the API root. */
export function timeOffEnrollmentEndPath(employmentId: string, enrollmentId: string): string {
  return `${timeOffEnrollmentsPath(employmentId)}/${encodeURIComponent(enrollmentId)}/end`;
}

/** The plan kinds, accrual periods and bases the schema's enums hold (decision 0110). */
export const PTO_PLAN_KINDS = ['Vacation', 'Sick', 'Personal', 'Bereavement', 'Unpaid'] as const;
export type PtoPlanKindName = (typeof PTO_PLAN_KINDS)[number];
export const PTO_ACCRUAL_PERIODS = ['PerPayPeriod', 'Monthly', 'AnnualGrant'] as const;
export type PtoAccrualPeriodName = (typeof PTO_ACCRUAL_PERIODS)[number];
export const PTO_ACCRUAL_BASES = ['Tenure', 'Flat', 'PerHourWorked'] as const;
export type PtoAccrualBasisName = (typeof PTO_ACCRUAL_BASES)[number];

/** 🔨 The bounds the columns and the decision hold: a cap multiple, a waiting period, a smallest request and an opening balance. */
export const PTO_CAP_MULTIPLIER_MAXIMUM = 999.99;
export const PTO_WAITING_DAYS_MAXIMUM = 3650;
export const PTO_INCREMENT_MINIMUM = 0.25;
export const PTO_INCREMENT_MAXIMUM = 24;
export const PTO_OPENING_HOURS_MAXIMUM = 99999.99;
export const PTO_PLAN_NAME_MAXIMUM = 80;
export const PTO_ENROLLMENT_REASON_MAXIMUM = 200;

/** One plan as the Settings page lists it. Each figure is a number; a cap of null caps nothing. */
export interface TimeOffPlanRow {
  id: string;
  name: string;
  planKind: PtoPlanKindName;
  accrualPeriod: PtoAccrualPeriodName;
  accrualBasis: PtoAccrualBasisName;
  capMultiplier: number | null;
  waitingPeriodDays: number;
  minIncrementHours: number;
  allowNegativeBalance: boolean;
  payoutOnSeparation: boolean;
  isActive: boolean;
  /** `YYYY-MM-DD`. */
  startDate: string;
  endDate: string | null;
}

/** What a plan's edit asks (decision 13130): the kind, the active flag and the dates are read only. */
export interface TimeOffPlanDraft {
  name: string;
  accrualPeriod: string;
  accrualBasis: string;
  capMultiplier: number | null;
  waitingPeriodDays: number | null;
  minIncrementHours: number | null;
  allowNegativeBalance: boolean;
  payoutOnSeparation: boolean;
}
export type TimeOffPlanField = keyof TimeOffPlanDraft;

/** 🔨 An edit sends the values it read beside the values it writes, and is refused where another save moved them (decision 13130). */
export interface TimeOffPlanEdit {
  read: TimeOffPlanDraft;
  draft: TimeOffPlanDraft;
}

/** The fields an edit compares and writes, in the order the audit row names them. */
export const TIME_OFF_PLAN_FIELDS: readonly TimeOffPlanField[] = ['name', 'accrualPeriod', 'accrualBasis', 'capMultiplier', 'waitingPeriodDays', 'minIncrementHours', 'allowNegativeBalance', 'payoutOnSeparation'];

/** The edit window's starting values: the plan as it was read. */
export function timeOffPlanDraft(row: TimeOffPlanRow): TimeOffPlanDraft {
  return {
    name: row.name, accrualPeriod: row.accrualPeriod, accrualBasis: row.accrualBasis, capMultiplier: row.capMultiplier,
    waitingPeriodDays: row.waitingPeriodDays, minIncrementHours: row.minIncrementHours, allowNegativeBalance: row.allowNegativeBalance, payoutOnSeparation: row.payoutOnSeparation,
  };
}

/** A figure with at most two places. */
const twoPlaces = (value: number) => Math.abs(value * 100 - Math.round(value * 100)) < 1e-6;

/** 🔨 What stops a plan's edit, by field, in a sentence each: a name, a period and a basis the schema holds, and each figure inside its bounds. */
export function timeOffPlanProblems(draft: TimeOffPlanDraft): Partial<Record<TimeOffPlanField, string>> {
  const problems: Partial<Record<TimeOffPlanField, string>> = {};
  const name = typeof draft.name === 'string' ? draft.name.trim() : '';
  if (name === '' || name.length > PTO_PLAN_NAME_MAXIMUM) problems.name = t('hr.timeOffPlans.problem.name');
  if (!(PTO_ACCRUAL_PERIODS as readonly string[]).includes(draft.accrualPeriod)) problems.accrualPeriod = t('hr.timeOffPlans.problem.period');
  if (!(PTO_ACCRUAL_BASES as readonly string[]).includes(draft.accrualBasis)) problems.accrualBasis = t('hr.timeOffPlans.problem.basis');
  const cap = draft.capMultiplier;
  if (cap !== null && (typeof cap !== 'number' || !Number.isFinite(cap) || cap <= 0 || cap > PTO_CAP_MULTIPLIER_MAXIMUM || !twoPlaces(cap))) problems.capMultiplier = t('hr.timeOffPlans.problem.cap');
  const wait = draft.waitingPeriodDays;
  if (typeof wait !== 'number' || !Number.isInteger(wait) || wait < 0 || wait > PTO_WAITING_DAYS_MAXIMUM) problems.waitingPeriodDays = t('hr.timeOffPlans.problem.waiting');
  const step = draft.minIncrementHours;
  if (typeof step !== 'number' || !Number.isFinite(step) || step < PTO_INCREMENT_MINIMUM || step > PTO_INCREMENT_MAXIMUM || !twoPlaces(step)) problems.minIncrementHours = t('hr.timeOffPlans.problem.increment');
  return problems;
}

/** 🔒 The refusal of the plans' read and edit to anybody not holding the hire permission. */
export const TIME_OFF_PLANS_REFUSAL = 'Time-off plans are kept by HR with the hire permission, and this identity does not hold it.';
/** 🔨 An edit whose read values another save has moved since. */
export const TIME_OFF_PLAN_MOVED_REFUSAL = 'Another save changed this plan after you opened it. Close the window and open the plan again to see it as it stands.';

/** One enrollment, as HR's Enroll and End read it. */
export interface TimeOffEnrollmentRow {
  id: string;
  planId: string;
  planName: string;
  /** `YYYY-MM-DD`, the first day the plan applies. */
  startDate: string;
  /** The last day the plan applies, or null while it runs on. */
  endDate: string | null;
  reason: string;
}

/** What Enroll sends (decision 13130). */
export interface TimeOffEnrollmentDraft {
  planId: string;
  /** `YYYY-MM-DD`. */
  startDate: string;
  /** The opening balance in hours, 0 to 99,999.99 at two places. */
  openingHours: number | null;
  reason: string;
}
export type TimeOffEnrollmentField = keyof TimeOffEnrollmentDraft;

/** What End sends: the last day the plan applies. */
export interface TimeOffEnrollmentEndDraft {
  endDate: string;
}

/**
 * 🔒 What the Job section carries for Enroll and End (decision 13130): sent to HR reading as HR with `hr.hire`
 * and to nobody else, so the buttons draw where the routes answer.
 */
export interface TimeOffEnrollmentSection {
  /** The active plans the job holds no enrollment in that runs on today, which Enroll offers. */
  plans: { id: string; name: string }[];
  /** Every enrollment of the job, newest first. */
  enrollments: TimeOffEnrollmentRow[];
  /** The job's own dates, which an enrollment date falls inside. */
  jobStartDate: string;
  jobEndDate: string | null;
  /** The job has ended, so it takes no new enrollment. */
  ended: boolean;
  /** The Organization's today, `YYYY-MM-DD`, Enroll's starting date where the job has begun. */
  today: string;
}

/** Enroll's starting values: the first plan offered, today or the job's first day where that is later, and nothing typed. */
export function timeOffEnrollmentStart(section: Pick<TimeOffEnrollmentSection, 'plans' | 'jobStartDate' | 'today'>): TimeOffEnrollmentDraft {
  return { planId: section.plans[0]?.id ?? '', startDate: section.jobStartDate > section.today ? section.jobStartDate : section.today, openingHours: null, reason: '' };
}

/** 🔨 What stops an enrollment, by field: a plan, a real day inside the job's dates, an opening balance from 0 to 99,999.99 at two places, and a reason. */
export function timeOffEnrollmentProblems(draft: TimeOffEnrollmentDraft, job?: { startDate: string; endDate: string | null }): Partial<Record<TimeOffEnrollmentField, string>> {
  const problems: Partial<Record<TimeOffEnrollmentField, string>> = {};
  if (typeof draft.planId !== 'string' || draft.planId === '') problems.planId = t('hr.timeOffPlans.problem.plan');
  if (typeof draft.startDate !== 'string' || !isCalendarDay(draft.startDate)) problems.startDate = t('hr.timeOff.problem.day');
  else if (job && draft.startDate < job.startDate) problems.startDate = fill(t('hr.timeOffPlans.problem.beforeJob'), { day: job.startDate });
  else if (job?.endDate && draft.startDate > job.endDate) problems.startDate = fill(t('hr.timeOffPlans.problem.afterJob'), { day: job.endDate });
  const hours = draft.openingHours;
  if (typeof hours !== 'number' || !Number.isFinite(hours) || hours < 0 || hours > PTO_OPENING_HOURS_MAXIMUM || !twoPlaces(hours)) problems.openingHours = t('hr.timeOffPlans.problem.opening');
  const reason = typeof draft.reason === 'string' ? draft.reason.trim() : '';
  if (reason === '' || reason.length > PTO_ENROLLMENT_REASON_MAXIMUM) problems.reason = t('hr.timeOffPlans.problem.reason');
  return problems;
}

/** 🔨 What stops an end: a real day on or after the enrollment's first day. */
export function timeOffEnrollmentEndProblem(draft: TimeOffEnrollmentEndDraft, enrollment: Pick<TimeOffEnrollmentRow, 'startDate'>): string | null {
  if (typeof draft.endDate !== 'string' || !isCalendarDay(draft.endDate)) return t('hr.timeOff.problem.day');
  if (draft.endDate < enrollment.startDate) return fill(t('hr.timeOffPlans.problem.endBeforeStart'), { day: enrollment.startDate });
  return null;
}

/** 🔒 The refusal of Enroll and End to anybody but HR reading as HR with the hire permission (decision 13130). */
export const TIME_OFF_ENROLL_REFUSAL = 'A job is enrolled in a time-off plan by HR with the hire permission, and this identity is not HR with it.';
/** 🔨 An ended job takes no new enrollment. */
export const TIME_OFF_ENROLL_ENDED_REFUSAL = 'This job has ended, so it takes no new time-off plan.';
/** 🔨 A plan that is not active is never enrolled in. */
export const TIME_OFF_ENROLL_INACTIVE_REFUSAL = 'That plan is not active, so no job is enrolled in it.';
/** 🔨 An enrollment already ended is not ended again. */
export const TIME_OFF_ENROLLMENT_ENDED_REFUSAL = 'This enrollment has already ended.';

/** 🔨 A second enrollment in a plan while one runs on the new day, naming the plan. */
export function timeOffSecondEnrollmentRefusal(planName: string): string {
  return fill('This job is already enrolled in {plan} on that day. End that enrollment first, or start the new one after it ends.', { plan: planName });
}

/** 🔨 End refused while a pending or approved request on the plan runs past the day, naming the request's days. */
export function timeOffEndPastRequestRefusal(firstDay: string, lastDay: string): string {
  return fill('A request from {first} to {last} runs past that day. Cancel or decide it first, or end the plan after its last day.', { first: firstDay, last: lastDay });
}

/** The Job section's pill for one enrollment, read against today. */
export function timeOffEnrollmentStanding(row: Pick<TimeOffEnrollmentRow, 'startDate' | 'endDate'>, today: string): 'later' | 'current' | 'ended' {
  if (row.startDate > today) return 'later';
  return row.endDate !== null && row.endDate < today ? 'ended' : 'current';
}
